'use client';
import { createContext, ReactNode, useCallback, useRef, useState } from 'react';

import { BoxButton, BoxButtonProps } from '../../button';

import { Dialog, DialogFooter, DialogTitle, DialogContent } from './Dialog';

export const DialogContext = createContext<DialogHandler>({
  showDialog: () =>
    new Promise(() => {
      return {
        id: '',
        selected: 'none'
      };
    }),
  closeDialog: () => {}
});

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [dialogIds, setDialogIds] = useState<string[]>([]);
  const currentDialogs = useRef<DialogData[]>([]);

  const showDialogRef = useRef<(dialogData: DialogData) => Promise<DialogPromiseResult>>((dialogData) => new Promise(() => {}));
  const closeDialogRef = useRef<(closeDialogId?: string) => void>(() => {});

  const resetDialogIds = useCallback(() => {
    setDialogIds(currentDialogs.current.map((dialog) => dialog.id as string));
  }, []);

  showDialogRef.current = (dialogData: DialogData) => {
    return new Promise<DialogPromiseResult>((resolve, reject) => {
      if (dialogData.id && currentDialogs.current.map((dialog) => dialog.id as string).includes(dialogData.id)) {
        reject(`${dialogData.id} promise dialog already exists`);
        return;
      }

      dialogData.resolve = resolve;
      if (!dialogData.id) {
        dialogData.id = Math.random().toString(36).substring(7);
      }

      currentDialogs.current.push(dialogData);
      resetDialogIds();
    });
  };

  closeDialogRef.current = (closeDialogId?: string) => {
    let dialogData: DialogData | undefined = undefined;
    if (!closeDialogId || dialogIds[dialogIds.length - 1] === closeDialogId) {
      dialogData = currentDialogs.current.pop();
    } else if (closeDialogId) {
      const dataIndex = dialogIds.findIndex((dialogId) => dialogId === closeDialogId);
      dialogData = currentDialogs.current[dataIndex];
      currentDialogs.current.splice(dataIndex, 1);
    }

    resetDialogIds();

    if (dialogData?.resolve) {
      dialogData?.resolve({ id: dialogData?.id as string, selected: 'none' });
    }
  };

  const showDialog = useCallback((dialogData: DialogData) => showDialogRef.current!(dialogData), []);
  const closeDialog = useCallback((closeDialogId?: string) => closeDialogRef.current!(closeDialogId), []);

  const onClickButton = (dialogData: DialogData, buttonType: 'primary' | 'secondary' = 'primary') => {
    const actionButton = dialogData.buttons?.[buttonType];
    let actionResult: unknown = undefined;

    if (actionButton?.onClick) {
      actionResult = actionButton.onClick();
    }

    dialogData.resolve?.({ id: dialogData.id as string, selected: buttonType, result: actionResult });
    dialogData.resolve = undefined;

    if (!actionButton?.isPreventAutoClose) {
      closeDialog(dialogData.id);
    }
  };

  return (
    <DialogContext.Provider value={{ showDialog, closeDialog }}>
      {children}
      {dialogIds.length > 0 &&
        currentDialogs.current.map((dialog) => (
          <Dialog key={dialog.id} open>
            <DialogContent className={'focus-visible:outline-none'}>
              <DialogTitle title={dialog.title} description={dialog.description || ''} />
              <DialogFooter className={'flex gap-normal-small !pt-2 px-normal-medium pb-normal-medium'}>
                {dialog.buttons?.secondary && (
                  <BoxButton
                    className={'flex-1'}
                    size={'medium'}
                    variant={dialog.buttons.secondary.variant ?? 'tonal-secondary'}
                    disabled={dialog.buttons.secondary.disabled}
                    onClick={() => onClickButton(dialog, 'secondary')}
                  >
                    {dialog.buttons.secondary.text ?? '취소'}
                  </BoxButton>
                )}
                <BoxButton
                  className={'flex-1'}
                  size={'medium'}
                  variant={dialog.buttons?.primary.variant ?? 'filled-primary'}
                  disabled={dialog.buttons?.primary.disabled}
                  onClick={() => onClickButton(dialog)}
                >
                  {dialog.buttons?.primary.text ?? '확인'}
                </BoxButton>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ))}
    </DialogContext.Provider>
  );
};

export type DialogData = {
  id?: string;
  title: string;
  description?: ReactNode;
  buttons?: {
    primary: DialogButton;
    secondary?: DialogButton;
  };
  resolve?: (value: DialogPromiseResult | PromiseLike<DialogPromiseResult>) => void;
};

type DialogButton = BoxButtonProps & {
  text?: string;
  onClick?: () => unknown;
  isPreventAutoClose?: boolean;
};

type DialogHandler = {
  showDialog: (dialogData: DialogData) => Promise<DialogPromiseResult>;
  closeDialog: (dialogId?: string) => void;
};

type DialogPromiseResult = { id: string; selected: 'primary' | 'secondary' | 'none'; result?: unknown };
